import { StrToStr, StrToBool } from './serialize-graph-types';
import { SceneGraphNode, HumanObjectTransform, HumanObjectRenderable } from '../../models/scene-graph-node';
import { renamespaceValue } from '../renamespace-utils';


export namespace SerializeGraphUtils {
  // serialize non-default values on human object properties
  export function serializeObjectProperties(humanObject: any, node: SceneGraphNode): void {
    ['backfaces', 'displayName', 'layer'].forEach((property) => {
      if (humanObject[property]) {
        node[property] = humanObject[property];
      }
    });
  }

  export function serializeTransformData(humanObject: any, node: SceneGraphNode): void {
    const transform = new HumanObjectTransform();
    transform.translate = JSON.parse(JSON.stringify(humanObject.translate));
    transform.rotate = JSON.parse(JSON.stringify(humanObject.rotate));
    transform.scale = JSON.parse(JSON.stringify(humanObject.scale));
    transform.pivot = JSON.parse(JSON.stringify(humanObject.pivot));
    transform.filter();

    Object.entries(transform).forEach(([transformType, transformVal]) => {
      node[transformType] = JSON.parse(JSON.stringify(transformVal));
    });
    if (node.transformId) { delete node.transformId; }
  }

  export function serializeRenderables(humanObject: any, node: SceneGraphNode, namespaceMap: StrToStr): SceneGraphNode {

    const assetTypes: string[] = ['geometryId', 'morphId', 'materialId'];
    const renderables: HumanObjectRenderable[] = [];

    if (humanObject.renderables) {
      humanObject.renderables.forEach((renderTarget) => {
        const renderable = new HumanObjectRenderable;
        // copy assets
        assetTypes.forEach((assetType) => {
          if (renderTarget[assetType]) {
            renderable[assetType] = renamespaceValue(renderTarget[assetType], namespaceMap);
          }
        });

        // other attributes: layer

        // TODO is this supposed to filter out layer's with 0?
        if (renderTarget.layer) {
          renderable.layer = renderTarget.layer;
        }

        // add
        renderables.push(renderable);
      });
    }

    // serialize on to scene graph node
    if (renderables.length === 1) {
      node = { ...node, ...renderables[0] };
    } else if (renderables.length > 1) {
      node.renderables = renderables;
    }

    return node;
  }


  export function serializeHumanObject(
    humanObject: any,
    namespaceMap: StrToStr,
    overwrite: boolean,
    excludeMap: StrToBool,
    replaceObjectId = false
  ): SceneGraphNode {


    if (excludeMap[humanObject.objectId]) {
      return null;
    }
    let result: SceneGraphNode = new SceneGraphNode();
    result.objectId = humanObject.objectId;

    // id
    // By default object IDs do not change.
    // The only time they would change is when making a deep copy of an entire model in scene editor.
    if (replaceObjectId) {
      Object.entries(namespaceMap)
        .forEach(([key, value]) => {
          const regex = new RegExp(`^${key}`);
          result.objectId = result.objectId.replace(regex, value);
        });
    }
    // properties
    serializeObjectProperties(humanObject, result);

    // transform data
    if (overwrite || !humanObject.transformId) {
      serializeTransformData(humanObject, result);
    } else if (humanObject.transformId) {
      result.transformId = renamespaceValue(humanObject.transformId, namespaceMap);
    }


    // object renderables
    result = serializeRenderables(humanObject, result, namespaceMap);

    // downward traversal
    if (humanObject.objects && humanObject.objects.length) { result.objects = []; }
    humanObject.objects.forEach((childObject) => {
      const childNode = serializeHumanObject(childObject, namespaceMap, overwrite, excludeMap, replaceObjectId);
      if (childNode !== null) {
        result.objects.push(childNode);
      }
    }, this);

    return result;
  }
}
